import api from './axios';

export interface TranslationProject {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTranslationProjectRequest {
  name: string;
  displayName: string;
  description?: string;
}

export interface UpdateTranslationProjectRequest {
  displayName?: string;
  description?: string;
  isActive?: boolean;
}

class TranslationProjectsService {
  // Get all projects
  async getProjects(): Promise<TranslationProject[]> {
    const response = await api.get('/translations/projects');
    return response.data;
  }

  // Get project by ID
  async getProject(projectId: string): Promise<TranslationProject> {
    const response = await api.get(`/translations/projects/${projectId}`);
    return response.data;
  }

  // Create new project
  async createProject(data: CreateTranslationProjectRequest): Promise<TranslationProject> {
    const response = await api.post('/translations/projects', data);
    return response.data;
  }

  // Update project
  async updateProject(projectId: string, data: UpdateTranslationProjectRequest): Promise<TranslationProject> {
    const response = await api.patch(`/translations/projects/${projectId}`, data);
    return response.data;
  }

  // Delete project
  async deleteProject(projectId: string): Promise<void> {
    await api.delete(`/translations/projects/${projectId}`);
  }

  // Get all translations for a project (grouped by languages)
  async getProjectTranslations(projectId: string): Promise<{ translations: Record<string, Record<string, string>> }> {
    const response = await api.get(`/translations/projects/${projectId}/translations`);
    return response.data;
  }

  // Import translations to project language
  async importProjectTranslations(
    projectId: string,
    languageId: string,
    translations: Record<string, string>
  ): Promise<{ imported: number }> {
    const response = await api.post(
      `/translations/projects/${projectId}/languages/${languageId}/import`,
      { translations }
    );
    return response.data;
  }

  // Create/Update translation in project
  async createProjectTranslation(
    projectId: string,
    languageId: string,
    key: string,
    value: string
  ): Promise<{ success: boolean }> {
    const response = await api.post(
      `/translations/projects/${projectId}/languages/${languageId}`,
      { key, value }
    );
    return response.data;
  }

  // Update translation in project (with upsert behavior)
  async updateProjectTranslation(
    projectId: string,
    languageId: string,
    key: string,
    value: string
  ): Promise<{ success: boolean }> {
    try {
      // Попытка обновить существующий перевод
      const response = await api.patch(
        `/translations/projects/${projectId}/languages/${languageId}/translations/${encodeURIComponent(key)}`,
        { value }
      );
      return response.data;
    } catch (error: unknown) {
      // Если перевод не найден (404), создаем новый
      if (error && typeof error === 'object' && 'response' in error && (error as { response?: { status?: number } }).response?.status === 404) {
        console.log(`Project translation '${key}' not found, creating new one`);
        // Используем bulk API для создания
        await api.post(
          `/translations/projects/${projectId}/languages/${languageId}/translations/bulk`,
          { 
            translations: {
              [key]: value
            }
          }
        );
        return { success: true };
      }
      throw error;
    }
  }

  // Delete translation in project
  async deleteProjectTranslation(
    projectId: string,
    languageId: string,
    key: string
  ): Promise<void> {
    await api.delete(
      `/translations/projects/${projectId}/languages/${languageId}/translations/${encodeURIComponent(key)}`
    );
  }

  // Bulk delete translation keys across all languages of a project
  async bulkDeleteProjectKeys(
    projectId: string,
    keys: string[]
  ): Promise<{ success: boolean; deletedCount: number; keysRequested: number }> {
    const response = await api.post(
      `/translations/projects/${projectId}/translations/bulk-delete`,
      { keys }
    );
    return response.data;
  }

  // Batch save translations for project
  async batchSaveProjectTranslations(
    projectId: string,
    translations: Array<{ languageId: string; key: string; value: string }>
  ): Promise<{ saved: number; errors: Array<{ key: string; error: string }> }> {
    // Group translations by language
    const translationsByLanguage = translations.reduce((acc, translation) => {
      if (!acc[translation.languageId]) {
        acc[translation.languageId] = {};
      }
      acc[translation.languageId][translation.key] = translation.value;
      return acc;
    }, {} as Record<string, Record<string, string>>);

    let totalSaved = 0;
    const allErrors: Array<{ key: string; error: string }> = [];

    // Send batch request for each language
    for (const [languageId, languageTranslations] of Object.entries(translationsByLanguage)) {
      try {
        const translationCount = Object.keys(languageTranslations).length;
        console.log(`📦 Batch saving ${translationCount} translations for language ${languageId}`);
        
        const response = await api.post(
          `/translations/projects/${projectId}/languages/${languageId}/translations/bulk`,
          { translations: languageTranslations }
        );
        
        console.log(`✅ Language ${languageId} batch result:`, response.data);
        totalSaved += response.data.saved || translationCount;
        
        if (response.data.errors) {
          allErrors.push(...response.data.errors);
        }
      } catch (error) {
        console.error(`❌ Batch save failed for language ${languageId}:`, error);
        // Add all translations of this language to errors
        Object.keys(languageTranslations).forEach(key => {
          allErrors.push({
            key: key,
            error: `Language ${languageId}: ${error instanceof Error ? error.message : 'Unknown error'}`
          });
        });
      }
    }

    return {
      saved: totalSaved,
      errors: allErrors
    };
  }
}

export const translationProjectsService = new TranslationProjectsService();
